<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  inboxId: { type: Number, required: true },
  placeholder: { type: String, default: '' },
});

const text = defineModel('text', { type: String, default: '' });
const audioUrl = defineModel('audioUrl', { type: String, default: '' });

const { t } = useI18n();
const recorder = ref(null);
const fileInput = ref(null);
const isRecording = ref(false);
const isUploading = ref(false);
const recordingTime = ref('00:00');

const upload = async file => {
  if (isUploading.value) return;

  isUploading.value = true;
  try {
    const { data } = await InboxesAPI.uploadVoiceGreeting(props.inboxId, file);
    audioUrl.value = data.url;
  } catch {
    useAlert(t('INBOX_MGMT.CALL_HANDLING.GREETING.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

// AudioRecorder starts capturing on mount without reporting a blocked microphone,
// so ask first: otherwise the recorder sits at 00:00 with no explanation.
const startRecording = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop());
  } catch {
    useAlert(t('INBOX_MGMT.CALL_HANDLING.GREETING.MIC_BLOCKED'));
    return;
  }
  recordingTime.value = '00:00';
  isRecording.value = true;
};

const onRecorded = ({ file }) => {
  isRecording.value = false;
  upload(file);
};

const onRecordError = () => {
  isRecording.value = false;
  useAlert(t('INBOX_MGMT.CALL_HANDLING.GREETING.UPLOAD_ERROR'));
};

const onFilePicked = event => {
  const [file] = event.target.files;
  event.target.value = '';
  if (file) upload(file);
};
</script>

<template>
  <div class="flex flex-col gap-2 w-full">
    <div v-if="isRecording" class="flex items-center gap-2">
      <AudioRecorder
        ref="recorder"
        audio-record-format="audio/mp3"
        class="flex-1 min-w-0 h-14 overflow-hidden"
        @recorder-progress-changed="time => (recordingTime = time)"
        @finish-record="onRecorded"
        @record-error="onRecordError"
      />
      <span class="text-label-small text-n-ruby-11 tabular-nums">
        {{
          $t('INBOX_MGMT.CALL_HANDLING.GREETING.RECORDING', {
            time: recordingTime,
          })
        }}
      </span>
      <Button
        size="sm"
        color="ruby"
        icon="i-lucide-square"
        :label="$t('INBOX_MGMT.CALL_HANDLING.GREETING.STOP')"
        @click="recorder.stopRecording()"
      />
    </div>
    <div v-else class="flex flex-wrap items-center gap-2">
      <audio v-if="audioUrl" :src="audioUrl" controls class="h-8 max-w-xs" />
      <Button
        size="sm"
        variant="faded"
        color="slate"
        icon="i-ph-microphone"
        :label="
          audioUrl
            ? $t('INBOX_MGMT.CALL_HANDLING.GREETING.RERECORD')
            : $t('INBOX_MGMT.CALL_HANDLING.GREETING.RECORD')
        "
        @click="startRecording"
      />
      <Button
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-upload"
        :is-loading="isUploading"
        :label="$t('INBOX_MGMT.CALL_HANDLING.GREETING.UPLOAD')"
        @click="fileInput.click()"
      />
      <Button
        v-if="audioUrl"
        size="sm"
        variant="ghost"
        color="ruby"
        icon="i-lucide-trash-2"
        :label="$t('INBOX_MGMT.CALL_HANDLING.GREETING.REMOVE')"
        @click="audioUrl = ''"
      />
      <input
        ref="fileInput"
        type="file"
        accept=".mp3,.wav,audio/mpeg,audio/wav"
        class="hidden"
        @change="onFilePicked"
      />
    </div>
    <p class="mb-0 text-label-small text-n-slate-11">
      {{
        audioUrl
          ? $t('INBOX_MGMT.CALL_HANDLING.GREETING.AUDIO_HINT')
          : $t('INBOX_MGMT.CALL_HANDLING.GREETING.TEXT_HINT')
      }}
    </p>
    <TextArea
      v-model="text"
      :placeholder="placeholder"
      auto-height
      max-height="none"
    />
  </div>
</template>
